/* webpack-strip-block:removed */

import * as TAGS_API from 'Lib/tags';

/**
 * @typedef VueTagsInputTag
 * @property { string } text the tag 'name'; what is displayed for this tag
 * @property { string[] } tiClasses css classes applied to the tag element
 */

/**
 * ## TaggableModelMixin.js
 *
 * A mixin which provides functionality to interact with tags, with an expectation to be used with [vue-multiselect](https://vue-multiselect.js.org/).
 *
 * Two data fields are tracked:
 * - `auto_complete_tags`: A list of all available tags which the user might select from (i.e in the multiselect dropdown)
 * - `ms_tag_values`: The 'currently selected' tags; usually this is the `v-model` of the multiselect
 *
 * General usage:
 * - assign existing tags to `ms_tag_values`, and bind the multiselect `v-model="ms_tag_values"`
 * - call `this.fetchTags`, which will populate `auto_complete_tags`
 * - if you want the user to be able to create new tags on the fly, also bind the `addNewTag` method to the `@tag` event: `@tag="addNewTag"`
 */
export default {
  data() {
    return {
      /** @type { Tag[] } */
      auto_complete_tags: [],
      /** @type { Tag[] } */
      ms_tag_values: [],
    };
  },
  methods: {
    /**
     * Fetches tags from the backend, optionally showing a toastr popup on success.
     *
     * These are then stored in the `auto_complete_tags` data field.
     *
     * @param { TagFetchParams } fetch_params API request params, i.e `per_page=10000`
     * @param { ?string } success_popup_message
     */
    async fetchTags(fetch_params, success_popup_message = null) {
      this.auto_complete_tags = await TAGS_API.fetchTags(fetch_params);
      if (success_popup_message) toastr.success(success_popup_message);
    },
    /**
     * Creates a new tag on the backend, and also pushes that new tag to `auto_complete_tags` and `ms_tag_values`.
     *
     * Tag names are automatically converted to lowercase.
     *
     * @param { string | { tag: string, admin_only: ?boolean} } tag
     */
    async addNewTag(new_tag) {
      /** @type { TagCreateDefinition } */
      const new_tag_definition = typeof new_tag === 'object'
        ? new_tag
        : {
          tag: new_tag,
          admin_only: false,
        };

      const [response] = await TAGS_API.createTags(new_tag_definition);
      const created_tag = response.data.data.tag;

      this.auto_complete_tags.push(created_tag);
      this.ms_tag_values.push(created_tag);
    },
  },
};
